import { gql } from '@apollo/client'
import posthog from 'posthog-js'

/**
 * Linking a Telegram login to an existing account (TR-837).
 *
 * Telegram gives us no email, so a user whose account was created with Google or a magic link is
 * not recognised when they log in with Telegram, and signup then fails with EMAIL_TAKEN. In that
 * case we keep their signed Telegram login here, send a magic link to the email, and once they are
 * signed in to their account we ask the backend to link the Telegram login to it.
 */
export const PENDING_TELEGRAM_LINK_KEY = 'pending_telegram_link'

export const LINK_TELEGRAM_LOGIN_MUTATION = gql`
  mutation LinkTelegramLogin($telegramAuth: String!) {
    linkTelegramLogin(telegramAuth: $telegramAuth)
  }
`

// Errors that will not go away on a retry, so the pending link is dropped.
const FINAL_LINK_ERRORS = [ 'LOGIN_IDENTITY_TAKEN', 'INVALID_TELEGRAM_AUTH', 'TELEGRAM_AUTH_EXPIRED' ]

export const isTelegramSession = (): boolean => localStorage.getItem('auth_type') === 'telegram'

/** Encodes the stored Telegram login the same way readToken() does for the `Telegram` auth header. */
const encodeTelegramLogin = (raw: string): string => btoa(unescape(encodeURIComponent(raw)))

/** Keeps the current Telegram login so it can be linked after the user confirms their email. */
export const savePendingTelegramLink = (): boolean => {
  const raw = localStorage.getItem('telegram_user')
  if (!raw) return false
  localStorage.setItem(PENDING_TELEGRAM_LINK_KEY, encodeTelegramLogin(raw))
  return true
}

type LinkTelegramLogin = (options: { variables: { telegramAuth: string } }) => Promise<unknown>

/**
 * Links a pending Telegram login to the signed-in account. Call once the current user has loaded.
 * Returns true when a Telegram login was linked.
 */
let linkInFlight: Promise<boolean> | null = null

export const linkPendingTelegramLogin = (linkTelegramLogin: LinkTelegramLogin): Promise<boolean> => {
  if (!linkInFlight) {
    linkInFlight = runLink(linkTelegramLogin).finally(() => {
      linkInFlight = null
    })
  }
  return linkInFlight
}

const runLink = async (linkTelegramLogin: LinkTelegramLogin): Promise<boolean> => {
  const telegramAuth = localStorage.getItem(PENDING_TELEGRAM_LINK_KEY)
  if (!telegramAuth || isTelegramSession()) return false

  try {
    await linkTelegramLogin({ variables: { telegramAuth } })
    localStorage.removeItem(PENDING_TELEGRAM_LINK_KEY)
    posthog.capture('telegram_login_linked')
    return true
  } catch (error) {
    const code = (error as { graphQLErrors?: { extensions?: { code?: string } }[] })
      ?.graphQLErrors?.[0]?.extensions?.code
    if (code && FINAL_LINK_ERRORS.includes(code)) {
      localStorage.removeItem(PENDING_TELEGRAM_LINK_KEY)
    }
    posthog.captureException(error, { feature: 'link_telegram_login', code })
    return false
  }
}
